import { CheckboxList, Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { useSelector } from "react-redux";
import type { HistoricNarRecommendation } from "../../../../../../shared/api/services/historicNar/types";
import { ModalId } from "../../../../../../shared/enums/modalId";
import { RootState } from "../../../../../redux/store/store";
import {
  capitalizeString,
  capitalizeWordsSeperatedByUnderScore,
} from "../../../../../utils/stringModifications";
import useConfirmSourceIngest from "../hooks/useConfirmSourceIngest";
import type { AutomationProps } from "./automation";
import HistoricNarAutomationFields from "./historicNarAutomationFields";
import PayerPdfUpload from "./payerPdfUpload";
import SourceConfirmationFields from "./sourceConfirmationFields";

interface ConfirmSourceIngestModalProps {
  metaData?: { clientOptions: AutomationProps["options"] };
  onClose: () => void;
  onIngested: () => void;
}

/** Confirm, configure automation, and ingest the selected recommendation. */
const ConfirmSourceIngestModal = ({
  metaData,
  onClose,
  onIngested,
}: ConfirmSourceIngestModalProps) => {
  const row = useSelector((state: RootState) => state.modal.metaData) as
    HistoricNarRecommendation | undefined;
  const {
    title,
    isSaving,
    disableSave,
    handleIngest,
    source,
    payerPdf,
    expiration,
    automation,
  } = useConfirmSourceIngest({ row, onClose, onIngested });

  return (
    <Modal
      dialogId={ModalId.HISTORIC_NAR_CONFIRM_SOURCE_MODAL}
      title={title}
      onSave={handleIngest}
      onCancel={onClose}
      onClose={onClose}
      saveButtonText={isSaving ? "Ingesting..." : "Ingest to Auth Grid"}
      cancelText="Cancel"
      disableSave={disableSave}
      maxHeightPercentage={70}
    >
      <div className="confirm_source_ingest_modal--container flex flex-col gap-4">
        <div className="border-b border-primaryGray-15 pb-2 text-small text-primaryGray-3">
          {capitalizeWordsSeperatedByUnderScore(row?.payer ?? "")} ·{" "}
          {capitalizeString(row?.line_of_business)} · {row?.j_code}{" "}
          {row?.medicine_name} · {row?.month}
        </div>
        <SourceConfirmationFields {...source} />
        <PayerPdfUpload {...payerPdf} />
        <div className="border-b border-primaryGray-15 pb-3">
          <CheckboxList
            items={[
              {
                id: "historic-never-expire",
                label:
                  "Never expire (row stays valid instead of ending at month-end)",
                checked: expiration.neverExpire,
              },
            ]}
            onSelectionChange={(items) => expiration.onChange(items[0].checked)}
            showSelectAll={false}
            ariaLabel="Never expire"
            className="!w-full text-wrap !p-0"
          />
        </div>
        <HistoricNarAutomationFields
          options={metaData?.clientOptions ?? []}
          {...automation}
        />
      </div>
    </Modal>
  );
};

export default ConfirmSourceIngestModal;
